/**
 * Wires the resource-mutation `runner.*` capabilities to the live session's
 * `ConnectorManager` + `PreMintedSecretProvider`. Kept free of any `serve.ts`
 * import so the dependency direction stays acyclic (same rule as
 * `runner-capabilities.ts`). The caller passes getters (not values) because
 * `serve.ts` lazily (re)assigns `resourceManager` / `preMintedSecrets`.
 *
 * @category Runtime
 * @since 0.38.0
 */
import { join } from "node:path";
import {
  type ConnectorDeclaration,
  type ConnectorManager,
  deriveSingleMaterializedConnectorDeclaration,
  type PreMintedCredential,
  type PreMintedSecretProvider,
} from "@skaile/workspaces/connectors";
import type { RunnerCapabilityHandlers } from "./runner-capabilities.js";

export interface SessionResourceHandlerDeps {
  /** The live unified connector manager, or null before the session is built. */
  getResourceManager: () => ConnectorManager | null;
  /** The in-process pre-minted credential store, or undefined before init. */
  getPreMintedSecrets: () => PreMintedSecretProvider | undefined;
  /** Re-emit `resources_available` so the frontend reflects the live change. */
  emitResourcesAvailable: () => void;
  log: (msg: string) => void;
  /**
   * Absolute path to the session project directory. Required by
   * `attachConnectorInstance` for the materialized-asset lookup under
   * `<projectDir>/.skaile/assets/connector/<name>`.
   */
  getProjectDir?: () => string;
}

type AddResult = { ok: true; id: string } | { ok: false; code: string; message: string };
type MutateResult = { ok: true } | { ok: false; code: string; message: string };

/** Attach-by-ref result — shape parity with the MCP `attachInstance` return. */
export type AttachResult =
  | { ok: true; id: string; live: boolean; toolCount: number }
  | { ok: false; code: string; message: string };

/** A connector/mount declaration is usable only if it carries id + driver. */
function asDeclaration(declaration: unknown): ConnectorDeclaration | null {
  if (
    declaration !== null &&
    typeof declaration === "object" &&
    typeof (declaration as { id?: unknown }).id === "string" &&
    typeof (declaration as { driver?: unknown }).driver === "string"
  ) {
    return declaration as ConnectorDeclaration;
  }
  return null;
}

export function toPreMinted(token: {
  token: string;
  expiresAt?: string | null;
}): PreMintedCredential {
  return { ok: true, token: token.token, expiresAt: token.expiresAt ?? null };
}

export function buildSessionResourceHandlers(deps: SessionResourceHandlerDeps): Pick<
  RunnerCapabilityHandlers,
  "addConnector" | "addMount" | "removeResource" | "updateCredential"
> & {
  attachConnectorInstance: (
    name: string,
    preMintedToken?: { token: string; expiresAt?: string | null },
  ) => Promise<AttachResult>;
} {
  const { getResourceManager, getPreMintedSecrets, emitResourcesAvailable, log, getProjectDir } =
    deps;

  async function add(
    kind: "mount" | "connector",
    input: {
      declaration: unknown;
      preMintedToken?: { token: string; expiresAt?: string | null };
    },
  ): Promise<AddResult> {
    const rm = getResourceManager();
    if (!rm) return { ok: false, code: "no_session", message: "session resources not ready" };
    const decl = asDeclaration(input.declaration);
    if (!decl) {
      return { ok: false, code: "invalid_declaration", message: "declaration missing id/driver" };
    }
    // Stage the credential before connect — ConnectorManager consults the
    // provider for the `auth: backend` initial-mint path inside connect().
    if (input.preMintedToken) {
      const ps = getPreMintedSecrets();
      if (ps) ps.set(kind, decl.id, toPreMinted(input.preMintedToken));
      // Without the store the credential is silently dropped and connect() will
      // fail its backend-auth mint with no trace — log the lost token.
      else
        log(
          `[session-resource] preMintedToken provided but secret store not ready — ${kind}:${decl.id}`,
        );
    }
    try {
      await rm.connect(decl);
    } catch (err) {
      if (input.preMintedToken) {
        getPreMintedSecrets()?.unset(kind, decl.id);
      }
      const message = err instanceof Error ? err.message : String(err);
      log(`[session-resource] runner.add_${kind} failed for ${decl.id}: ${message}`);
      return { ok: false, code: "connect_failed", message };
    }
    emitResourcesAvailable();
    log(`[session-resource] runner.add_${kind} connected ${decl.id}`);
    return { ok: true, id: decl.id };
  }

  /**
   * Materialize-then-attach-by-ref for `kind: connector`. The platform has
   * written `<projectDir>/.skaile/assets/connector/<name>/CONNECTOR.md`; the
   * runner self-derives the declaration, stages the wire-delivered credential
   * (no secret on disk), and connects it live via `ConnectorManager.connect`.
   * Mirrors the MCP `attachInstance` + `add()` paths.
   */
  async function attachConnectorInstance(
    name: string,
    preMintedToken?: { token: string; expiresAt?: string | null },
  ): Promise<AttachResult> {
    if (!name) {
      return { ok: false, code: "invalid_input", message: "name is required" };
    }
    if (name.includes("/") || name.includes("\\") || name.includes("..")) {
      return {
        ok: false,
        code: "invalid_input",
        message: "name must not contain path separators or traversal segments",
      };
    }
    const projectDir = getProjectDir?.();
    if (!projectDir) {
      return { ok: false, code: "no_project_dir", message: "getProjectDir not configured" };
    }
    const root = join(projectDir, ".skaile/assets/connector");
    const decl = deriveSingleMaterializedConnectorDeclaration(root, name);
    if (!decl) {
      return {
        ok: false,
        code: "not_materialized",
        message: `no materialized connector at ${join(root, name)}`,
      };
    }
    const rm = getResourceManager();
    if (!rm) return { ok: false, code: "no_session", message: "session resources not ready" };
    // Stage the wire-delivered credential before connect — off-disk by design
    // (Phase 2.5b); ConnectorManager consults the provider for the
    // `auth: backend` initial-mint path inside connect().
    if (preMintedToken) {
      const ps = getPreMintedSecrets();
      if (ps) ps.set("connector", decl.id, toPreMinted(preMintedToken));
      // Without the store the credential is silently dropped and connect() will
      // fail its backend-auth mint with no trace — log the lost token.
      else
        log(
          `[session-resource] preMintedToken provided but secret store not ready — connector:${decl.id}`,
        );
    }
    try {
      await rm.connect(decl);
    } catch (err) {
      if (preMintedToken) {
        getPreMintedSecrets()?.unset("connector", decl.id);
      }
      const message = err instanceof Error ? err.message : String(err);
      log(`[session-resource] runner.attach_instance connector failed for ${decl.id}: ${message}`);
      return { ok: false, code: "connector_connect_failed", message };
    }
    emitResourcesAvailable();
    log(`[session-resource] runner.attach_instance connected connector ${decl.id}`);
    return { ok: true, id: decl.id, live: true, toolCount: 0 };
  }

  return {
    addConnector: (input) => add("connector", input),
    addMount: (input) => add("mount", input),
    attachConnectorInstance,

    removeResource: async (input): Promise<MutateResult> => {
      const rm = getResourceManager();
      if (!rm) return { ok: false, code: "no_session", message: "session resources not ready" };
      if (!rm.has(input.id)) {
        return { ok: false, code: "not_found", message: `no resource with id ${input.id}` };
      }
      try {
        await rm.disconnect(input.id);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        log(`[session-resource] runner.remove_resource failed for ${input.id}: ${message}`);
        return { ok: false, code: "disconnect_failed", message };
      }
      // Drop the staged credential under both namespaces — the id space is
      // shared, so we don't know which kind owned it.
      // TODO(mcp-detach): removeResource covers connector/mount only; mcp: namespace cleanup lands with runner.remove_mcp_server.
      const ps = getPreMintedSecrets();
      ps?.unset("connector", input.id);
      ps?.unset("mount", input.id);
      emitResourcesAvailable();
      log(`[session-resource] runner.remove_resource removed ${input.id}`);
      return { ok: true };
    },

    updateCredential: async (input): Promise<MutateResult> => {
      const ps = getPreMintedSecrets();
      if (!ps) return { ok: false, code: "no_session", message: "session resources not ready" };
      const rm = getResourceManager();
      if (rm && !rm.has(input.id)) {
        return { ok: false, code: "not_found", message: `no resource with id ${input.id}` };
      }
      // CredentialMint's success branch is structurally a PreMintedCredential;
      // staging it lets the next token resolution pick up the rotated value
      // without a reconnect (full propagation to live rclone/git = Sprint 3).
      ps.set(input.kind, input.id, input.mint as unknown as PreMintedCredential);
      log(`[session-resource] runner.update_credential staged ${input.kind}:${input.id}`);
      return { ok: true };
    },
  };
}
